import { inject } from "@angular/core";
import { Actions, ROOT_EFFECTS_INIT, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { auditTime, distinctUntilChanged, map, skip, tap } from "rxjs";

import { DatadogService } from "../services/datadog.service";

import { getPersistenceDiagnostics, runBestEffort } from "./persist-state-diagnostics";
import { selectPersistedState } from "./persist-state.selectors";
import { PERSIST_KEY, removeStalePersistedState } from "./persist-state.utils";

export const cleanupStalePersistedState = createEffect((
  actions$ = inject(Actions),
) => {
  return actions$.pipe(
    ofType(ROOT_EFFECTS_INIT),
    tap(() => removeStalePersistedState()),
  );
}, { functional: true, dispatch: false });

export const persistState = createEffect((
  store = inject(Store),
  datadog = inject(DatadogService),
) => {
    // Requirement: Let only storage writes change degradation state, suppress
    // repeated failure telemetry, and report recovery after a successful write.
    let persistenceDegraded = false;

    return store.select(selectPersistedState).pipe(
      skip(1),
      auditTime(200),
      map((state) => ({
        state,
        stringifiedState: JSON.stringify(state),
      })),
      distinctUntilChanged((previous, current) => previous.stringifiedState === current.stringifiedState),
      tap(({ state, stringifiedState }) => {
        try {
          localStorage.setItem(PERSIST_KEY, stringifiedState);
        } catch (error) {
          if (persistenceDegraded) {
            return;
          }

          persistenceDegraded = true;
          runBestEffort(() => {
            const diagnostics = getPersistenceDiagnostics(stringifiedState, state.jobs, state.queue);
            const errorName = error instanceof Error ? error.name : 'UnknownError';

            console.error('Recovery persistence degraded', {
              ...diagnostics,
              errorName,
            });
            datadog.addAction('persist_write_failed', {
              ...diagnostics,
              error_name: errorName,
            });
          });
          return;
        }

        if (persistenceDegraded) {
          persistenceDegraded = false;
          runBestEffort(() => {
            console.info('Recovery persistence restored');
            datadog.addAction('persist_write_recovered');
          });
        }
      }),
    );
  }, { functional: true, dispatch: false });
